import { Table, Button } from "react-bootstrap";
import { Skills, sum } from "../models/Skills";
import { useMemo, useState } from "react";
import { GameData, GameDataItem } from "../models/GameData";
import { sortBy } from "lodash";
import { levelsFromXp } from "../models/UserData";
import { SkillDetailsModal } from "./SkillDetailsModal";

export const SkillTable = ({ xps, gameData, rankedSkillXps }: { xps: Skills; gameData: GameData, rankedSkillXps?: Skills[] }): React.ReactElement => {
    const levels: Skills = useMemo(() => levelsFromXp(xps), [xps]);
    const [selectedSkillId, setSelectedSkillId] = useState<keyof Skills>();
    const bestGamesPerSkill: Record<string, GameDataItem[]> = useMemo(() => {
        const topGames = gameData.games.slice(0, 500);
        const gamesPerSkill: Record<string, GameDataItem[]> = {};
        for (const skill in levels) {
            gamesPerSkill[skill] = sortBy(topGames, (game) => -1 * (game.xp[skill as keyof Skills] ?? 0) / sum(game.xp)).slice(0, 10);
        }
        return gamesPerSkill;
    }, [gameData.games, levels]);
    const rankPerSkill = useMemo(() => {
        const rankPerSkill: Record<keyof Skills, string> = {
            a: "",
            b: "",
            c: "",
            d: "",
            e: "",
            f: "",
            g: "",
            h: "",
            i: "",
            j: "",
            k: "",
            l: "",
            m: "",
            n: "",
            o: ""
        };
        for (const skill of Object.keys(xps) as (keyof typeof xps)[]) {
            const skillXp = xps[skill] ?? 0;

            if (skillXp === 0) {
                continue;
            }

            const skillXpRanks = [rankedSkillXps?.[0]?.[skill] ?? 0, rankedSkillXps?.[1]?.[skill] ?? 0, rankedSkillXps?.[2]?.[skill] ?? 0];
            if (skillXp >= skillXpRanks[0]) {
                rankPerSkill[skill] = '🥇';
            } else if (skillXp >= skillXpRanks[1]) {
                rankPerSkill[skill] = '🥈';
            } else if (skillXp >= skillXpRanks[2]) {
                rankPerSkill[skill] = '🥉';
            }
        }
        return rankPerSkill;
    }, [rankedSkillXps]);

    return (
        <>
            {!!selectedSkillId && <SkillDetailsModal onHide={() => setSelectedSkillId(undefined)} id={selectedSkillId} games={bestGamesPerSkill[selectedSkillId]} xp={xps[selectedSkillId] ?? 0} rank={rankPerSkill[selectedSkillId]} />}
            <Table borderless>
                <tbody>
                    <tr>
                        <SkillRowByDefinition id='a' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('a')} />
                        <SkillRowByDefinition id='b' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('b')} />
                        <SkillRowByDefinition id='c' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('c')} />
                    </tr>
                    <tr>
                        <SkillRowByDefinition id='d' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('d')} />
                        <SkillRowByDefinition id='e' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('e')} />
                        <SkillRowByDefinition id='f' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('f')} />
                    </tr>
                    <tr>
                        <SkillRowByDefinition id='g' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('g')} />
                        <SkillRowByDefinition id='h' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('h')} />
                        <SkillRowByDefinition id='i' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('i')} />
                    </tr>
                    <tr>
                        <SkillRowByDefinition id='j' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('j')} />
                        <SkillRowByDefinition id='k' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('k')} />
                        <SkillRowByDefinition id='l' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('l')} />
                    </tr>
                    <tr>
                        <SkillRowByDefinition id='m' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('m')} />
                        <SkillRowByDefinition id='n' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('n')} />
                        <SkillRowByDefinition id='o' levels={levels} ranks={rankPerSkill} onClick={() => setSelectedSkillId('o')} />
                    </tr>
                    <tr>
                        <SkillRow disabled colSpan={3} title='Total Level' onClick={() => { }}><strong>Total Level</strong><div>{sum(levels)}</div></SkillRow>
                    </tr>
                </tbody>
            </Table>
        </>
    )
}

export const SkillImageIcon = ({ name, size = 's' }: { name: string; size?: 's' | 'lg' }) => {
    let sizeNum = 32;
    switch (size) {
        case 's':
            sizeNum = 32;
            break;
        case 'lg':
            sizeNum = 64;
            break;
    }
    return <img width={sizeNum} height={sizeNum} alt={`${name} skill icon`} src={`/play-tracker/skill-icons/${name}.png`} />
}

export class SkillDefinition {
    public name: string;
    public description: string;
    public icon: string | React.ReactElement;
    public iconLarge: string | React.ReactElement;

    public constructor(name: string, description: string) {
        this.name = name;
        this.description = description;
        this.icon = <SkillImageIcon name={this.name} />;
        this.iconLarge = <SkillImageIcon name={this.name} size='lg' />;
    }
}

export const SKILLS: Record<keyof Skills | string, SkillDefinition> = {
    a: new SkillDefinition('Deduction', 'Drawing conclusions based on a set of premises or assumptions.'),
    b: new SkillDefinition('Math', 'Calculating probabilities and general math.'),
    c: new SkillDefinition('Optimization', 'Generating the most value with the fewest resources.'),

    d: new SkillDefinition('Pattern Matching', 'Identifying or creating patterns or sets.'),
    e: new SkillDefinition('Planning', 'Long term decision making and sequence plotting.'),
    f: new SkillDefinition('Risk Mitigation', 'Risk versus value decision making.'),

    g: new SkillDefinition('Strategy', 'Short term tactical decision making and unit movement.'),
    h: new SkillDefinition('Luck', 'Getting desireable results unrelated to any choices being made.'),
    i: new SkillDefinition('Card Management', 'Building decks and drafting cards.'),

    j: new SkillDefinition('Dexterity', 'Physical control or the ability to carefully manipulate objects.'),
    k: new SkillDefinition('Dice Rolling', 'Rolling dice in any capacity.'),
    l: new SkillDefinition('Memory', 'Remembering information learned within a game or trivia learned outside the game.'),

    m: new SkillDefinition('Deception', 'Manipulating others to believe something that is untrue.'),
    n: new SkillDefinition('Teamwork', 'Working with others or sharing their intuition.'),
    o: new SkillDefinition('Persuasion', 'Convicing others to take an action you desire.'),
};

export const SkillRowByDefinition = ({ id, levels, ranks, onClick }: { id: keyof Skills; levels: Skills, ranks: Record<keyof Skills, string>, onClick: () => void; }): React.ReactElement => {
    return <SkillRow onClick={onClick} title={SKILLS[id].name} bordercolor={RANK_COLORS[ranks[id]]} borderwidth={ranks[id] ? '2px' : '1px'}>{SKILLS[id].icon} {levels[id]}</SkillRow>
}

export const SkillRow = ({ children, onClick, disabled, ...props }: { onClick: () => void; children?: React.ReactNode; title?: string; colSpan?: number; disabled?: boolean, bordercolor?: string, borderwidth?: string }): React.ReactElement => {
    return <td {...props}><Button onClick={onClick} disabled={disabled} variant='outline-dark' style={{ display: 'block', width: '100%', borderColor: props.bordercolor, borderWidth: props.borderwidth }}>{children}</Button></td>
}

const RANK_COLORS: Record<string, string> = Object.freeze({
    '🥇': 'gold',
    '🥈': 'silver',
    '🥉': '#cd7f32'
});
